import {
  normalizeDisplayCurrency,
  normalizeMoneyValue,
} from "../domain/money";

export const CHECKOUT_V2_SET_STEP = "CHECKOUT_V2_SET_STEP";
export const CHECKOUT_V2_DISMISS_CART_CHANGES = "CHECKOUT_V2_DISMISS_CART_CHANGES";

const sanitizeDismissedCartChanges = (keys) => Array.from(new Set(
  (Array.isArray(keys) ? keys : []).filter(key => typeof key === "string" && key.length <= 1000),
)).slice(-200);
export const CHECKOUT_V2_CLEAR_STEP = "CHECKOUT_V2_CLEAR_STEP";
export const CHECKOUT_V2_OPEN_AUTH = "CHECKOUT_V2_OPEN_AUTH";
export const CHECKOUT_V2_CLOSE_AUTH = "CHECKOUT_V2_CLOSE_AUTH";
export const CHECKOUT_V2_START_PAYMENT_ATTEMPT =
  "CHECKOUT_V2_START_PAYMENT_ATTEMPT";
export const CHECKOUT_V2_UPDATE_PAYMENT_ATTEMPT =
  "CHECKOUT_V2_UPDATE_PAYMENT_ATTEMPT";
export const CHECKOUT_V2_CLEAR_PAYMENT_ATTEMPT =
  "CHECKOUT_V2_CLEAR_PAYMENT_ATTEMPT";
export const CHECKOUT_V2_RESET = "CHECKOUT_V2_RESET";
export const CHECKOUT_V2_SET_CONTEXT = "CHECKOUT_V2_SET_CONTEXT";
export const CHECKOUT_V2_PATCH_DRAFT = "CHECKOUT_V2_PATCH_DRAFT";
export const CHECKOUT_V2_SET_REMOVED_LINES = "CHECKOUT_V2_SET_REMOVED_LINES";
export const CHECKOUT_V2_SET_QUOTE = "CHECKOUT_V2_SET_QUOTE";
export const CHECKOUT_V2_CLEAR_QUOTE = "CHECKOUT_V2_CLEAR_QUOTE";
export const CHECKOUT_V2_SET_CART_VALIDATION =
  "CHECKOUT_V2_SET_CART_VALIDATION";
export const CHECKOUT_V2_CLEAR_CART_VALIDATION =
  "CHECKOUT_V2_CLEAR_CART_VALIDATION";

const CART_CONTENT_ACTIONS = new Set([
  "ADD_To_CART",
  "ADD_PROMO_ITEM",
  "CLEAR_CART",
  "EDIT_PRODUCT",
  "EDIT_QUANTITY",
  "EDIT_QUANTITY_PROMO",
  "REMOVE_FROM_CART",
  "REMOVE_FROM_CART_PROMO_ITEM",
  "EDIT_CART", "EDIT_CART_PROMO_ITEM", "EDIT_OPTION", "EDIT_BOOKING", "LOAD_CART_JSON",
]);

export const CHECKOUT_V2_STEPS = Object.freeze([
  "contact",
  "fulfillment",
  "address",
  "map",
  "extras",
  "payment",
  "review",
]);

const PAYMENT_STATUSES = Object.freeze([
  "created",
  "redirected",
  "pending",
  "succeeded",
  "failed",
]);

const AUTH_METHODS = Object.freeze([
  "email",
  "phone_otp",
  "apple",
  "google",
]);

const safeString = (value, maxLength = 160) =>
  `${value || ""}`.trim().slice(0, maxLength);

const safeTimestamp = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

export const sanitizeCheckoutPath = (value, fallback = "/checkout") => {
  const path = safeString(value, 500);
  if (
    !path.startsWith("/") ||
    path.startsWith("//") ||
    path.includes("\\") ||
    Array.from(path).some((character) => character.charCodeAt(0) < 32)
  )
    return fallback;
  return path;
};

export const sanitizeCheckoutStep = (value) =>
  CHECKOUT_V2_STEPS.includes(value) ? value : null;

export const sanitizePaymentAttempt = (attempt) => {
  if (!attempt || typeof attempt !== "object") return null;
  const trackingId = safeString(attempt.trackingId || attempt.tracking_id, 120);
  const attemptId = safeString(attempt.attemptId || trackingId, 120);
  if (!trackingId || !attemptId) return null;

  return {
    attemptId,
    trackingId,
    method: safeString(attempt.method, 80),
    status: PAYMENT_STATUSES.includes(attempt.status)
      ? attempt.status
      : "created",
    createdAt: safeTimestamp(attempt.createdAt),
    updatedAt: safeTimestamp(attempt.updatedAt),
    errorCode: safeString(attempt.errorCode, 100) || null,
    retryKey: safeString(attempt.retryKey, 120) || null,
    retryPhase: ["requesting", "redirected"].includes(attempt.retryPhase)
      ? attempt.retryPhase
      : null,
    returnPath: sanitizeCheckoutPath(attempt.returnPath),
  };
};

const sanitizeAuthOverlay = (overlay = {}) => ({
  open: overlay.open === true,
  method: AUTH_METHODS.includes(overlay.method) ? overlay.method : null,
  mode: overlay.mode === "signup" ? "signup" : "login",
  source: safeString(overlay.source, 80) || null,
  returnTo: sanitizeCheckoutPath(overlay.returnTo),
});

const safeId = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const normalized = safeString(value, 120);
  if (!normalized) return null;
  return /^-?\d+$/.test(normalized) ? parseInt(normalized, 10) : normalized;
};

export const sanitizeFulfillmentContext = (context) => {
  if (!context || typeof context !== "object") return null;
  const type = ["delivery", "pickup"].includes(context.type)
    ? context.type
    : null;
  const pickupMode =
    type === "pickup" && ["inside", "car"].includes(context.pickupMode)
      ? context.pickupMode
      : null;
  return {
    version: 1,
    type,
    pickupMode,
    isBranchBased: context.isBranchBased === true,
    countryId: type === "delivery" ? safeId(context.countryId) : null,
    areaId: type === "delivery" ? safeId(context.areaId) : null,
    branchId: safeId(context.branchId),
    branchDeliveryChargeId:
      type === "delivery" ? safeId(context.branchDeliveryChargeId) : null,
    time: {
      type: ["asap", "scheduled"].includes(context.time?.type)
        ? context.time.type
        : null,
      date: safeString(context.time?.date, 20),
      start: safeString(context.time?.start, 20),
      end: safeString(context.time?.end, 20),
      ...(context.time?.withinMinutes !== null &&
      context.time?.withinMinutes !== undefined &&
      Number.isFinite(Number(context.time.withinMinutes))
        ? {
            withinMinutes: Math.max(0, Number(context.time.withinMinutes)),
          }
        : {}),
      ...(safeString(context.time?.authority, 120)
        ? { authority: safeString(context.time.authority, 120) }
        : {}),
    },
    fingerprint: safeString(context.fingerprint, 80) || null,
  };
};

export const sanitizeCheckoutDraft = (draft = {}) => ({
  initialized: draft.initialized === true,
  extrasAcknowledged: draft.extrasAcknowledged === true,
  noCutlery: draft.noCutlery === true,
  giftWrapping: draft.giftWrapping === true,
  useCustomerWallet: draft.useCustomerWallet === true,
  useCustomerPoints: draft.useCustomerPoints === true,
  selectedPaymentMethod: safeString(draft.selectedPaymentMethod, 80),
});

export const sanitizeRemovedLineIds = (lineIds = []) =>
  Array.from(
    new Set(
      (Array.isArray(lineIds) ? lineIds : [])
        .map((lineId) => safeString(lineId, 120))
        .filter(Boolean),
    ),
  ).slice(0, 100);

export const sanitizeQuoteMetadata = (quote) => {
  if (!quote || typeof quote !== "object") return null;
  const safeMoney = (value) =>
    normalizeMoneyValue(value, { currency: quote.currency });
  const errors = (Array.isArray(quote.errors) ? quote.errors : [])
    .map((error) => {
      if (error && typeof error === "object") {
        const code = safeString(error.code || error.reason, 120);
        const message = safeString(error.message, 240);
        return code || message
          ? { code: code || "checkout_unavailable", message: message || null }
          : null;
      }
      const code = safeString(error, 120);
      return code ? { code, message: null } : null;
    })
    .filter(Boolean)
    .slice(0, 20);
  return {
    quoteId: safeString(quote.quoteId || quote.quote_id, 120) || null,
    refreshing: quote.refreshing === true,
    pricingStatus:
      safeString(quote.pricingStatus || quote.pricing_status, 40) === "pending"
        ? "pending"
        : "ready",
    pendingReasons: (Array.isArray(
      quote.pendingReasons || quote.pending_reasons,
    )
      ? quote.pendingReasons || quote.pending_reasons
      : []
    )
      .map((reason) => safeString(reason, 120))
      .filter(Boolean)
      .slice(0, 20),
    valid: quote.valid === true,
    clientCartFingerprint: safeString(quote.clientCartFingerprint, 80) || null,
    requiresRequote:
      quote.requiresRequote === true || quote.requires_requote === true,
    errors,
    contextFingerprint:
      safeString(
        quote.contextFingerprint || quote.context_fingerprint,
        80,
      ) || null,
    currency: safeString(quote.currency, 12) || null,
    displayCurrency: normalizeDisplayCurrency(
      quote.displayCurrency || quote.display_currency,
    ),
    subtotal: safeMoney(quote.subtotal),
    discountTotal: safeMoney(quote.discountTotal ?? quote.discount_total),
    deliveryRate: safeMoney(quote.deliveryRate ?? quote.delivery_rate),
    vatAmount: safeMoney(quote.vatAmount ?? quote.vat_amount),
    additionalFee: safeMoney(quote.additionalFee ?? quote.additional_fee),
    giftWrapping: safeMoney(quote.giftWrapping ?? quote.gift_wrapping),
    orderTotal: safeMoney(quote.orderTotal ?? quote.order_total),
    walletApplied: safeMoney(quote.walletApplied ?? quote.wallet_applied),
    pointsApplied: safeMoney(quote.pointsApplied ?? quote.points_applied),
    total: safeMoney(quote.total),
    minimumOrderRemaining: safeMoney(
      quote.minimumOrderRemaining ??
        quote.movRemaining ??
        quote.mov_remaining,
    ),
    meetsMinimum:
      quote.meetsMinimum === true || quote.meets_minimum === true,
    paymentMethods: (Array.isArray(
      quote.paymentMethods || quote.payment_methods,
    )
      ? quote.paymentMethods || quote.payment_methods
      : []
    )
      .map((method) =>
        safeString(typeof method === "object" ? method.id : method, 80),
      )
      .filter(Boolean)
      .slice(0, 50),
    paymentMethodOptions: (Array.isArray(quote.paymentMethodOptions)
      ? quote.paymentMethodOptions
      : []
    )
      .map((method) => ({
        id: safeString(method.id, 80),
        eligible: method.eligible === true,
        reason: safeString(method.reason, 200) || null,
        label: safeString(method.label, 120) || null,
        arLabel:
          safeString(method.arLabel || method.ar_label, 120) || null,
        type: safeString(method.type, 40) || null,
      }))
      .filter((method) => method.id)
      .slice(0, 50),
    expiresAt: safeTimestamp(quote.expiresAt || quote.expires_at),
    calculatedAt: safeTimestamp(
      quote.calculatedAt || quote.calculated_at,
    ),
    version: safeString(quote.version, 40) || null,
  };
};

const sanitizeValidationLine = (line = {}) => ({
  ...(line.name ? { name: safeString(line.name, 200) } : {}),
  ...(line.arName ? { arName: safeString(line.arName, 200) } : {}),
  lineId: safeString(line.lineId || line.line_id, 120),
  productId: safeId(line.productId || line.product_id || line.id),
  quantity:
    line.quantity === null || line.quantity === undefined
      ? null
      : Number.isFinite(Number(line.quantity))
        ? Number(line.quantity)
        : null,
  originalQuantity:
    line.originalQuantity === null || line.originalQuantity === undefined
      ? null
      : Number.isFinite(Number(line.originalQuantity))
        ? Number(line.originalQuantity)
        : null,
  reason: safeString(line.reason, 200) || null,
  restoreAllowed: line.restoreAllowed === true,
  ...(line.priceQuote
    ? { priceQuote: {
        unitPrice: normalizeMoneyValue(line.priceQuote.unitPrice),
        lineTotal: normalizeMoneyValue(line.priceQuote.lineTotal),
      } }
    : {}),
});

export const sanitizeCartValidation = (validation) => {
  if (!validation || typeof validation !== "object") return null;
  return {
    valid: validation.valid === true,
    contextFingerprint:
      safeString(
        validation.contextFingerprint || validation.context_fingerprint,
        80,
      ) || null,
    cartFingerprint:
      safeString(
        validation.cartFingerprint || validation.cart_fingerprint,
        80,
      ) || null,
    retainedLineIds: sanitizeRemovedLineIds(
      validation.retainedLineIds || validation.retained_line_ids,
    ),
    retainedLines: (Array.isArray(validation.retainedLines)
      ? validation.retainedLines
      : []
    )
      .map(sanitizeValidationLine)
      .filter((line) => line.lineId)
      .slice(0, 100),
    adjustedLines: (Array.isArray(validation.adjustedLines)
      ? validation.adjustedLines
      : []
    )
      .map(sanitizeValidationLine)
      .filter((line) => line.lineId)
      .slice(0, 100),
    removedLines: (Array.isArray(validation.removedLines)
      ? validation.removedLines
      : []
    )
      .map(sanitizeValidationLine)
      .filter((line) => line.lineId)
      .slice(0, 100),
    errors: (Array.isArray(validation.errors) ? validation.errors : [])
      .map((error) => safeString(error, 240))
      .filter(Boolean)
      .slice(0, 20),
    pricingStatus:
      safeString(
        validation.pricingStatus || validation.pricing_status,
        40,
      ) === "pending"
        ? "pending"
        : "ready",
    pendingReasons: (
      Array.isArray(validation.pendingReasons || validation.pending_reasons)
        ? validation.pendingReasons || validation.pending_reasons
        : []
    )
      .map((reason) => safeString(reason, 120))
      .filter(Boolean)
      .slice(0, 20),
    displayCurrency: normalizeDisplayCurrency(
      validation.displayCurrency || validation.display_currency,
    ),
    minimumOrderValue: normalizeMoneyValue(validation.minimumOrderValue),
    deliveryRate: normalizeMoneyValue(validation.deliveryRate),
    subtotal: normalizeMoneyValue(validation.subtotal),
    minimumOrderRemaining: normalizeMoneyValue(
      validation.minimumOrderRemaining,
    ),
    checkedAt: safeTimestamp(validation.checkedAt),
  };
};

export const initialCheckoutV2State = Object.freeze({
  version: 1,
  requestedStep: null,
  fulfillmentContext: null,
  draft: Object.freeze({
    initialized: false,
    extrasAcknowledged: false,
    noCutlery: false,
    giftWrapping: false,
    useCustomerWallet: false,
    useCustomerPoints: false,
    selectedPaymentMethod: "",
  }),
  removedLineIds: Object.freeze([]),
  dismissedCartChanges: Object.freeze([]),
  cartValidation: null,
  quote: null,
  authOverlay: Object.freeze({
    open: false,
    method: null,
    mode: "login",
    source: null,
    returnTo: "/checkout",
  }),
  paymentAttempt: null,
});

export const sanitizeCheckoutV2State = (state = {}) => ({
  version: 1,
  requestedStep: sanitizeCheckoutStep(state.requestedStep),
  fulfillmentContext: sanitizeFulfillmentContext(state.fulfillmentContext),
  draft: sanitizeCheckoutDraft(state.draft),
  removedLineIds: sanitizeRemovedLineIds(state.removedLineIds),
  dismissedCartChanges: sanitizeDismissedCartChanges(state.dismissedCartChanges),
  cartValidation: sanitizeCartValidation(state.cartValidation),
  quote: sanitizeQuoteMetadata(state.quote),
  authOverlay: sanitizeAuthOverlay(state.authOverlay),
  paymentAttempt: sanitizePaymentAttempt(state.paymentAttempt),
});

export default (state = initialCheckoutV2State, { type, payload }) => {
  if (CART_CONTENT_ACTIONS.has(type))
    return {
      ...state,
      cartValidation: null,
      removedLineIds: [],
      quote: null,
    };
  switch (type) {
    case "RECONCILE_CHECKOUT_CART":
      return { ...state, quote: null };
    case CHECKOUT_V2_DISMISS_CART_CHANGES:
      return { ...state, dismissedCartChanges: sanitizeDismissedCartChanges([
        ...(state.dismissedCartChanges || []), ...sanitizeDismissedCartChanges(payload),
      ]) };
    case CHECKOUT_V2_SET_STEP:
      return { ...state, requestedStep: sanitizeCheckoutStep(payload) };
    case CHECKOUT_V2_CLEAR_STEP:
      return { ...state, requestedStep: null };
    case CHECKOUT_V2_SET_CONTEXT:
      return { ...state, fulfillmentContext: sanitizeFulfillmentContext(payload) };
    case CHECKOUT_V2_PATCH_DRAFT:
      return {
        ...state,
        draft: sanitizeCheckoutDraft({ ...state.draft, ...payload }),
      };
    case CHECKOUT_V2_SET_REMOVED_LINES:
      return { ...state, removedLineIds: sanitizeRemovedLineIds(payload) };
    case CHECKOUT_V2_SET_QUOTE:
      return { ...state, quote: sanitizeQuoteMetadata(payload) };
    case CHECKOUT_V2_CLEAR_QUOTE:
      return { ...state, quote: null };
    case CHECKOUT_V2_SET_CART_VALIDATION: {
      const cartValidation = sanitizeCartValidation(payload);
      return {
        ...state,
        cartValidation,
        removedLineIds: sanitizeRemovedLineIds(
          cartValidation?.removedLines.map((line) => line.lineId) || [],
        ),
      };
    }
    case CHECKOUT_V2_CLEAR_CART_VALIDATION:
      return { ...state, cartValidation: null, removedLineIds: [] };
    case CHECKOUT_V2_OPEN_AUTH:
      return {
        ...state,
        authOverlay: sanitizeAuthOverlay({ ...payload, open: true }),
      };
    case CHECKOUT_V2_CLOSE_AUTH:
      return {
        ...state,
        authOverlay: { ...initialCheckoutV2State.authOverlay },
      };
    case CHECKOUT_V2_START_PAYMENT_ATTEMPT:
      return { ...state, paymentAttempt: sanitizePaymentAttempt(payload) };
    case CHECKOUT_V2_UPDATE_PAYMENT_ATTEMPT: {
      if (!state.paymentAttempt) return state;
      const nextAttempt = sanitizePaymentAttempt({
        ...state.paymentAttempt,
        ...payload,
        attemptId: state.paymentAttempt.attemptId,
        trackingId: state.paymentAttempt.trackingId,
        createdAt: state.paymentAttempt.createdAt,
      });
      return { ...state, paymentAttempt: nextAttempt };
    }
    case CHECKOUT_V2_CLEAR_PAYMENT_ATTEMPT:
      return { ...state, paymentAttempt: null };
    case CHECKOUT_V2_RESET:
    case "RESET_TO_INITIAL_STATE":
      return { ...initialCheckoutV2State };
    default:
      return state;
  }
};
